/***********************************************
 * useAudioMeter
 *
 * Monitors the volume level of a media stream
 *
 * volumeLevel: reference for volume percentage
 * initAudioMeter(stream): Set the audio stream
 * closeAudioMeter(): Cleanup the connected resources
 ***********************************************/
import { ref, watch } from "vue";
export const useAudioMeter = (stream) => {
    const volumeLevel = ref(0);
    // Analyser settings - based on live meeting meter
    const frequency = 50;
    const analyserSmoothingTime = 0.8;
    const analyserFftSize = 2048;
    const analyser = ref();
    let microphone = null;
    let audioContext = null;
    let volumeJob = null;
    const streamFailed = ref(false);
    const deadAudio = ref(false);
    watch(stream, (n) => {
        console.log("AUDIOMETER - Stream update", stream.value?.getAudioTracks());
        closeAudioMeter();
        if (n) {
            initAudioMeter();
            if (volumeJob) {
                clearInterval(volumeJob);
            }
            volumeJob = setInterval(updateVolume, frequency);
        }
    });
    let resetAttempt = 0;
    const closeAudioMeter = () => {
        if (volumeJob) {
            clearInterval(volumeJob);
            volumeJob = null;
        }
        streamFailed.value = false;
        count = 0;
        total = 0;
        microphone?.disconnect();
        analyser.value?.disconnect();
        audioContext?.close();
        analyser.value = undefined;
        microphone = null;
        audioContext = null;
        volumeLevel.value = 0;
    };
    let currentStreamId = "";
    const initAudioMeter = () => {
        if (!stream.value || stream.value.getAudioTracks().length == 0)
            return;
        listen_count = 0;
        listen_total = 0;
        if (stream.value.id != currentStreamId) {
            count = 0;
            total = 0;
            resetAttempt = 0;
        }
        currentStreamId = stream.value.id;
        if (audioContext == null) {
            audioContext = new AudioContext();
        }
        analyser.value = audioContext.createAnalyser();
        analyser.value.smoothingTimeConstant = analyserSmoothingTime;
        analyser.value.fftSize = analyserFftSize;
        microphone = audioContext.createMediaStreamSource(stream.value);
        microphone.connect(analyser.value);
    };
    let count = 0;
    let total = 0;
    let listen_count = 0;
    let listen_total = 0;
    const streamEnabled = () => {
        return stream.value?.getAudioTracks().at(0)?.enabled ?? false;
    };
    const updateVolume = () => {
        if (analyser.value) {
            if (!streamEnabled() || listen_count > 200) {
                listen_count = 0;
                listen_total = 0;
            }
            if (!streamEnabled()) {
                count = 0;
                total = 0;
                return;
            }
            if (!deadAudio.value && listen_total == 0 && listen_count >= 100) {
                console.log("Dead Audio from mic");
                deadAudio.value = true;
            }
            // Workaround - Safari sometimes just fails to initialize the audio analyser
            // Further investigaation needed
            // Will bubble up and reset stream recording as well
            // Need to prevent thrashing and set a limit to alert user their mic is possibly bad.
            if (resetAttempt < 3 && total == 0 && count >= 10 && streamEnabled()) {
                console.log("Failed to capture");
                streamFailed.value = true;
                resetAttempt++;
                return;
            }
            const array = new Uint8Array(analyser.value.frequencyBinCount);
            analyser.value.getByteFrequencyData(array);
            const sum = array.reduce((a, b) => a + b, 0);
            //console.log(sum);
            if (sum > 0)
                deadAudio.value = false;
            listen_count++;
            listen_total += sum;
            count++;
            total += sum;
            volumeLevel.value = sum / array.length;
        }
    };
    return {
        volumeLevel,
        deadAudio,
        analyser,
        streamFailed
    };
};
